import type { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import { ExpandLess, ExpandMore, Visibility } from '@mui/icons-material';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { handleSuccessToast } from 'src/components/molecules/Toast';
import {
  MitigationPlanFragment,
  useRecallMitigationPlanMutation,
  useRejectMitigationPlanMutation,
  useSignMitigationPlanMutation,
} from 'src/graphql';
import * as Types from '../../../types';
import { MitigationPlanDetailsDrawer } from './MitigationPlanDetailsDrawer';

const PLAN_STATUS_COLOR: Record<
  string,
  'generic' | 'warning' | 'success' | 'error'
> = {
  DRAFT: 'generic',
  PENDING: 'warning',
  SIGNED: 'success',
  CANCELLED: 'generic',
  OVERDUE: 'error',
};

interface Props {
  plan: MitigationPlanFragment;
  userMap: Record<number, { name: string; role?: string }>;
  onRecalled?: () => void;
  onEditClick?: (plan: MitigationPlanFragment) => void;
  onPlanUpdated?: () => void;
  canEdit?: boolean;
}

export const MitigationPlanCard = ({
  plan,
  userMap,
  onRecalled,
  onEditClick,
  onPlanUpdated,
  canEdit = false,
}: Props) => {
  const [expanded, setExpanded] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [rejectConfirmOpen, setRejectConfirmOpen] = useState(false);
  const [rejectionNote, setRejectionNote] = useState('');
  const { tokens, presets } = useTheme<LensTheme>();
  const color = tokens.semantic.color;
  const StatusIndicator =
    presets.StatusIndicatorPresets?.components.StatusIndicator;

  const currentUserId = useSelector(
    (state: Types.State) => state.globalUsers.currentUser?.id,
  );

  const recallMutation = useRecallMitigationPlanMutation();
  const signMutation = useSignMitigationPlanMutation({
    onSuccess: () => {
      handleSuccessToast({
        toastMessage: 'Mitigation plan approved successfully',
      });
      onPlanUpdated?.();
    },
  });
  const rejectMutation = useRejectMitigationPlanMutation({
    onSuccess: () => onPlanUpdated?.(),
  });

  const statusColor = PLAN_STATUS_COLOR[plan.status] ?? 'generic';
  const deadline = new Date(plan.deadline).toLocaleDateString();
  const submittedAt = new Date(plan.createdAt).toLocaleDateString();

  const reporter = plan.signatories.find((s) => s.isSubject);
  const additionalSignatories = plan.reviewerSignatories ?? [];

  const isSubmitted = plan.status !== 'DRAFT';

  const canSign =
    currentUserId !== undefined &&
    (plan.status === 'PENDING' || plan.status === 'OVERDUE') &&
    additionalSignatories.some(
      (s) => !s.signedAt && !s.rejectedAt && s.userId === Number(currentUserId),
    );

  const canRecall =
    (plan.status === 'PENDING' || plan.status === 'OVERDUE') &&
    plan.signatories.every((s) => !s.signedAt && !s.rejectedAt) &&
    canEdit;

  const handleRecall = async () => {
    try {
      await recallMutation.mutateAsync({ planId: plan.id });
      onRecalled?.();
    } catch {
      // error surfaced via recallMutation.isError
    }
  };

  return (
    <Box
      sx={{
        border: `1px solid ${color.ui.divider.default.value}`,
        borderRadius: tokens.semantic.radius.md.value,
        padding: '16px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Typography
            fontWeight="bold"
            data-testid="mitigation-plan-card-title"
            onClick={
              plan.status === 'DRAFT' && onEditClick
                ? () => onEditClick(plan)
                : undefined
            }
            onKeyDown={
              plan.status === 'DRAFT' && onEditClick
                ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onEditClick(plan);
                    }
                  }
                : undefined
            }
            role={plan.status === 'DRAFT' && onEditClick ? 'button' : undefined}
            tabIndex={plan.status === 'DRAFT' && onEditClick ? 0 : undefined}
            sx={
              plan.status === 'DRAFT' && onEditClick
                ? { cursor: 'pointer', textDecoration: 'underline' }
                : undefined
            }
          >
            {plan.title}
          </Typography>
          {StatusIndicator && (
            <StatusIndicator
              color={statusColor}
              label={plan.status.charAt(0) + plan.status.slice(1).toLowerCase()}
              data-testid="mitigation-plan-status-indicator"
            />
          )}
        </Box>
        <Typography
          variant="body2"
          color={
            plan.status === 'OVERDUE'
              ? color.action.destructive.default.value
              : color.type.muted.value
          }
        >
          Deadline: {deadline}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: '4px',
        }}
      >
        <Typography variant="body2" color={color.type.muted.value}>
          Submitted on: <strong>{submittedAt}</strong>
        </Typography>
        <IconButton
          size="small"
          onClick={() => setExpanded((e) => !e)}
          data-testid="mitigation-plan-card-expand"
          aria-label={
            expanded ? 'Collapse plan details' : 'Expand plan details'
          }
        >
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>

      <Collapse in={expanded} unmountOnExit>
        <Box
          sx={{
            mt: '16px',
            borderTop: `1px solid ${color.ui.divider.default.value}`,
            pt: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}
        >
          {reporter && (
            <Box>
              <Typography
                variant="caption"
                color={color.type.muted.value}
                sx={{ mb: '4px', display: 'block' }}
              >
                Reporter
              </Typography>
              <SignatoryRow signatory={reporter} userMap={userMap} />
            </Box>
          )}

          {additionalSignatories.length > 0 && (
            <Box>
              <Typography
                variant="caption"
                color={color.type.muted.value}
                sx={{ mb: '4px', display: 'block' }}
              >
                Additional signatories
              </Typography>
              {additionalSignatories.map((s) => (
                <SignatoryRow key={s.id} signatory={s} userMap={userMap} />
              ))}
              {canSign && (
                <Box sx={{ mt: '8px' }}>
                  {(signMutation.isError || rejectMutation.isError) && (
                    <Alert severity="error" sx={{ mb: '8px', py: '2px' }}>
                      {signMutation.isError
                        ? 'Failed to approve. Please try again.'
                        : 'Failed to reject. Please try again.'}
                    </Alert>
                  )}
                  <Box sx={{ display: 'flex', gap: '8px' }}>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={
                        signMutation.isLoading || rejectMutation.isLoading
                      }
                      onClick={() => setRejectConfirmOpen(true)}
                      data-testid="reject-mitigation-plan-card-button"
                      sx={{
                        textTransform: 'none',
                        borderColor: color.action.destructive.default.value,
                        color: color.action.destructive.default.value,
                        '&:hover': {
                          borderColor: color.action.destructive.default.value,
                          backgroundColor: 'transparent',
                        },
                      }}
                    >
                      Reject
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      disabled={
                        signMutation.isLoading || rejectMutation.isLoading
                      }
                      onClick={() => signMutation.mutate({ planId: plan.id })}
                      data-testid="sign-mitigation-plan-button"
                      sx={{ textTransform: 'none' }}
                    >
                      Approve plan
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {recallMutation.isError && (
            <Alert severity="error">
              Failed to recall the plan. Please try again.
            </Alert>
          )}

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px',
              mt: '4px',
            }}
          >
            {canRecall && (
              <Button
                variant="outlined"
                size="small"
                onClick={handleRecall}
                disabled={recallMutation.isLoading}
                data-testid="recall-mitigation-plan-button"
                sx={{
                  textTransform: 'none',
                  borderRadius: tokens.semantic.radius.lg.value,
                  borderColor: color.action.destructive.default.value,
                  color: color.action.destructive.default.value,
                  '&:hover': {
                    borderColor: color.action.destructive.default.value,
                    backgroundColor: 'transparent',
                  },
                }}
              >
                {recallMutation.isLoading ? 'Recalling…' : 'Recall'}
              </Button>
            )}
            {isSubmitted && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<Visibility fontSize="small" />}
                onClick={() => setDetailsOpen(true)}
                data-testid="mitigation-plan-view-details-button"
                sx={{
                  textTransform: 'none',
                  borderRadius: tokens.semantic.radius.lg.value,
                  borderColor: color.ui.divider.default.value,
                  color: color.type.default.value,
                  '&:hover': {
                    borderColor: color.type.default.value,
                    backgroundColor: 'transparent',
                  },
                }}
              >
                View plan details
              </Button>
            )}
          </Box>
        </Box>
      </Collapse>

      <Dialog
        open={rejectConfirmOpen}
        onClose={() => {
          setRejectConfirmOpen(false);
          setRejectionNote('');
        }}
        data-testid="reject-mitigation-plan-card-confirm-dialog"
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Reject mitigation plan?</DialogTitle>
        <DialogContent
          sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
        >
          <DialogContentText>
            Rejecting will keep the case under review. The reviewer lead can
            then assign a revised mitigation.
          </DialogContentText>
          <TextField
            label="Rejection reason"
            multiline
            minRows={3}
            required
            fullWidth
            value={rejectionNote}
            onChange={(e) => setRejectionNote(e.target.value)}
            inputProps={{ 'data-testid': 'rejection-note-card-input' }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRejectConfirmOpen(false);
              setRejectionNote('');
            }}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={rejectionNote.trim().length === 0}
            onClick={() => {
              const note = rejectionNote.trim();
              setRejectConfirmOpen(false);
              setRejectionNote('');
              rejectMutation.mutate({ planId: plan.id, note });
            }}
            data-testid="reject-mitigation-plan-card-confirm-button"
            sx={{ textTransform: 'none', color: '#fff' }}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      <MitigationPlanDetailsDrawer
        open={detailsOpen}
        plan={plan}
        onClose={() => setDetailsOpen(false)}
        onPlanUpdated={onPlanUpdated}
      />
    </Box>
  );
};

interface SignatoryRowProps {
  signatory: MitigationPlanFragment['signatories'][number];
  userMap: Record<number, { name: string; role?: string }>;
}

const SignatoryRow = ({ signatory, userMap }: SignatoryRowProps) => {
  const { tokens, presets } = useTheme<LensTheme>();
  const StatusIndicator =
    presets.StatusIndicatorPresets?.components.StatusIndicator;
  const mapUser = userMap[signatory.userId];
  const name =
    (signatory.user?.displayName || signatory.user?.name) ??
    mapUser?.name ??
    `User ${signatory.userId}`;
  const initials = name
    .split(' ')
    .slice(0, 2)
    .map((n) => n[0])
    .join('');
  const signed = Boolean(signatory.signedAt);
  const rejected = Boolean(signatory.rejectedAt);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: '4px',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Avatar sx={{ width: 32, height: 32, fontSize: '12px' }}>
          {initials}
        </Avatar>
        <Box>
          <Typography variant="body2" fontWeight={500}>
            {name}
          </Typography>
          {mapUser?.role && (
            <Typography
              variant="caption"
              color={tokens.semantic.color.type.muted.value}
            >
              {mapUser.role}
            </Typography>
          )}
        </Box>
      </Box>
      {StatusIndicator && (
        <StatusIndicator
          color={signed ? 'success' : rejected ? 'error' : 'warning'}
          label={signed ? 'Approved' : rejected ? 'Rejected' : 'Pending'}
          data-testid="mitigation-plan-signatory-status-indicator"
        />
      )}
    </Box>
  );
};
